import { Injectable, inject, signal } from '@angular/core';
import {
  AsyncSubject,
  defer,
  forkJoin,
  Observable,
  of,
  Subscription,
  throwError
} from 'rxjs';
import { BusinessService } from '@App/common/services';
import { AppSettings } from '@Shared/app.settings';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { catchError, map, tap } from 'rxjs/operators';
import { CardPackageService } from '../cards-packages/services';
import { EmployerPortalService } from '../choosen-history/services';
import {
  BusinessStatusEnum,
  DefaultCityStateEnum,
  EducationLevel,
  PitchCardTypeEnum,
  Step
} from '@App/common/enums';
import { StorageService } from '@Shared/services';
import { CustomValidators } from '@App/shared/utils';
import { educationLevelOption, introTextRequired } from './create-tile.utils';

@Injectable({ providedIn: 'root' })
export class CreatePitchCardService {
  business: any;
  readonly missingPitchCardMessage =
    'Unable to load PitchCard. Open a PitchCard from your account.';

  private readonly _loaded = signal(false);
  readonly loaded = this._loaded.asReadonly();
  private readonly _previewBusiness = signal<any>(null);
  readonly previewBusiness = this._previewBusiness.asReadonly();
  private previewSub?: Subscription;
  private paymentPlan: any = null;

  pitchcardFormControl = new FormGroup<{
    [key: string]: AbstractControl;
    pitchcardType: FormControl<PitchCardTypeEnum>;
  }>({
    pitchcardType: new FormControl<PitchCardTypeEnum>(null)
  });

  private readonly storageService = inject(StorageService);
  private readonly cardPackageService = inject(CardPackageService);
  private readonly businessService = inject(BusinessService);
  private readonly employerPortalService = inject(EmployerPortalService);
  private readonly pendingSaveJobs: Array<{
    fields: string[];
    request: Observable<any>;
    result: AsyncSubject<any>;
  }> = [];
  private readonly activeSaveFields = new Set<string>();

  clearEditor() {
    this.previewSub?.unsubscribe();
    this.previewSub = undefined;
    this.business = null;
    this.paymentPlan = null;
    this._previewBusiness.set(null);
    this._loaded.set(false);
  }

  isBusinessId(id: unknown): id is string {
    return typeof id === 'string' && id.trim().length > 0 && id !== 'null';
  }

  initializeFormGroups() {
    this.clearEditor();

    const businessId = this.storageService.getSession(
      AppSettings.DRAFT_BUSINESS_ID
    );

    if (!this.isBusinessId(businessId)) {
      return throwError(() => new Error(this.missingPitchCardMessage));
    }

    return forkJoin({
      business: this.businessService.getBusinessDetail(businessId),
      plan: this.businessService
        .getPaymentPlan(businessId)
        .pipe(catchError(() => of(null)))
    }).pipe(
      tap(({ business, plan }) => {
        if (!this.isBusinessId(business?.data?.id)) {
          throw new Error(this.missingPitchCardMessage);
        }

        this.paymentPlan =
          plan?.data?.notPaid ?? plan?.data?.paid ?? plan?.data ?? null;
        this.applyBusiness(business.data);
      }),
      map(() => this.pitchcardFormControl),
      tap(() => this._loaded.set(true)),
      catchError((error) => {
        this.storageService.setSession(AppSettings.DRAFT_BUSINESS_ID, null);
        this._loaded.set(false);
        return throwError(() =>
          error instanceof Error
            ? error
            : new Error(error?.message || this.missingPitchCardMessage)
        );
      })
    );
  }

  initializeEmployerPortalFormGroups(organization: any) {
    this.clearEditor();

    if (!organization?.id) {
      return throwError(() => new Error('Unable to load employer portal.'));
    }

    this.business = {
      ...organization,
      businessType: PitchCardTypeEnum.EmployerPortal
    };
    this.rebuildGroups(PitchCardTypeEnum.EmployerPortal);
    this._loaded.set(true);

    return of(this.pitchcardFormControl);
  }

  setGroups() {
    const type = this.pitchcardFormControl.controls.pitchcardType.value;

    if (type === PitchCardTypeEnum.Employee) {
      this.getEmployeeGroup();
    } else if (type === PitchCardTypeEnum.Resume) {
      this.getResumeGroup();
    } else if (type === PitchCardTypeEnum.Nonprofit) {
      this.getNonprofitGroup();
    } else if (type === PitchCardTypeEnum.EmployerPortal) {
      this.getEmployerPortalGroup();
    }
  }

  private getEmployerPortalGroup() {
    this.pitchcardFormControl.addControl(
      String(Step.Billing),
      this.getBilling()
    );
    this.pitchcardFormControl.addControl(
      String(Step.Information),
      this.createInformationGroup(PitchCardTypeEnum.EmployerPortal)
    );
    this.pitchcardFormControl.addControl(
      String(Step.About),
      this.createAboutGroup(PitchCardTypeEnum.EmployerPortal)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Uploads),
      this.createUploadsGroup(PitchCardTypeEnum.EmployerPortal)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Pitch),
      this.createPitchGroup(PitchCardTypeEnum.EmployerPortal)
    );
  }

  private shouldShowBillingStep(): boolean {
    return !this.paymentPlan?.isChargeBeePaymentPlan;
  }

  private applyBusiness(business: any) {
    this.business = business;
    const type = this.normalizePitchCardType(this.business.businessType);
    this.business.businessType = type;
    this.rebuildGroups(type);
  }

  private rebuildGroups(type: PitchCardTypeEnum) {
    this.resetStepControls();
    this.pitchcardFormControl.controls.pitchcardType.setValue(type);
    this.setGroups();
    this.bindPreviewToForm();
  }

  patchBusiness(value: Record<string, unknown>) {
    this.publishPreview({
      ...(this.business ?? {}),
      ...value
    });
  }

  saveBusinessField(field: string, value: unknown) {
    return this.saveBusinessFields({ [field]: value });
  }

  saveCompleteForm() {
    const payload = this.flattenFormValue(
      this.pitchcardFormControl.getRawValue()
    );
    delete payload.businessType;
    delete payload.cityName;
    delete payload.cityTbl;
    delete payload.stateTbl;
    return this.saveBusinessFields(payload);
  }

  finalizeEmployerPortal() {
    return this.saveBusinessFields({
      businessStatus: BusinessStatusEnum.Pending,
      cardDetails: null
    });
  }

  saveBusinessFields(fields: Record<string, unknown>) {
    const businessId =
      this.business?.id ||
      this.storageService.getSession(AppSettings.DRAFT_BUSINESS_ID);
    const payload = Object.fromEntries(
      Object.entries(fields ?? {}).filter(([field]) => !!field)
    );

    if (!this.isEntityId(businessId) || !Object.keys(payload).length) {
      return throwError(() => new Error('Unable to save changes'));
    }

    const businessType = this.pitchcardFormControl.controls.pitchcardType.value;
    const request =
      businessType === PitchCardTypeEnum.EmployerPortal
        ? this.employerPortalService.updateOrganizationByIndividualParameters(
            businessId,
            { businessType, ...payload }
          )
        : this.businessService.updateBusinessByIndividualParameters(
            String(businessId),
            { businessType, ...payload },
            false
          );

    const queuedRequest = request.pipe(
      tap((response) => {
        const data = response?.data;
        const savedData =
          businessType !== PitchCardTypeEnum.EmployerPortal &&
          this.isPlainObject(data)
            ? Object.fromEntries(
                Object.keys(payload)
                  .filter((field) => field in data)
                  .map((field) => [field, data[field]])
              )
            : {};
        if (data?.id && businessType !== PitchCardTypeEnum.EmployerPortal) {
          this.storageService.setSession(
            AppSettings.DRAFT_BUSINESS_ID,
            data.id
          );
        }
        this.business = {
          ...(this.business ?? {}),
          ...savedData,
          ...payload,
          ...(data?.id ? { id: data.id } : {})
        };
      })
    );

    return defer(() => {
      const result = new AsyncSubject<any>();
      this.pendingSaveJobs.push({
        fields: Object.keys(payload),
        request: queuedRequest,
        result
      });
      this.startPendingSaves();
      return result.asObservable();
    });
  }

  private startPendingSaves(): void {
    let nextIndex = this.pendingSaveJobs.findIndex(({ fields }) =>
      fields.every((field) => !this.activeSaveFields.has(field))
    );

    while (nextIndex !== -1) {
      const [job] = this.pendingSaveJobs.splice(nextIndex, 1);
      job.fields.forEach((field) => this.activeSaveFields.add(field));

      job.request.subscribe({
        next: (response) => job.result.next(response),
        error: (error) => {
          this.finishSaveJob(job.fields);
          job.result.error(error);
        },
        complete: () => {
          this.finishSaveJob(job.fields);
          job.result.complete();
        }
      });

      nextIndex = this.pendingSaveJobs.findIndex(({ fields }) =>
        fields.every((field) => !this.activeSaveFields.has(field))
      );
    }
  }

  private finishSaveJob(fields: string[]): void {
    fields.forEach((field) => this.activeSaveFields.delete(field));
    this.startPendingSaves();
  }

  private bindPreviewToForm() {
    this.previewSub?.unsubscribe();
    this.publishPreviewFromForm();
    this.previewSub = this.pitchcardFormControl.valueChanges.subscribe(() => {
      this.publishPreviewFromForm();
    });
  }

  private publishPreviewFromForm() {
    this.publishPreview({
      ...(this.business ?? {}),
      ...this.flattenFormValue(this.pitchcardFormControl.getRawValue())
    });
  }

  private publishPreview(business: any) {
    const current = this.business ?? {};
    const next = {
      ...current,
      ...business,
      businessStatus: business?.businessStatus ?? current.businessStatus,
      accountStatus:
        business?.accountStatus !== undefined
          ? business.accountStatus
          : current.accountStatus
    };
    this.business = next;
    this._previewBusiness.set(next);
  }

  private flattenFormValue(value: Record<string, any>): Record<string, any> {
    const patch: Record<string, any> = {};

    Object.entries(value ?? {}).forEach(([key, groupValue]) => {
      if (key === 'pitchcardType') {
        if (groupValue) {
          patch.businessType = groupValue;
        }
        return;
      }

      if (key === String(Step.Billing) || !this.isPlainObject(groupValue)) {
        return;
      }

      const {
        introductionOptions,
        referralIntroductionOptions,
        description,
        ...rest
      } = groupValue;

      Object.assign(patch, rest);

      if (this.isPlainObject(introductionOptions)) {
        Object.assign(patch, introductionOptions);
      }

      if (this.isPlainObject(referralIntroductionOptions)) {
        Object.assign(patch, referralIntroductionOptions);
      }

      if (key === String(Step.About) && description !== undefined) {
        patch.pricingModel = description;
      }
    });

    const phone = patch.contactPhone?.nationalNumber;
    if (phone) {
      patch.contactNumber = phone;
    }

    const current = this.business ?? {};
    if (
      typeof patch.city === 'string' &&
      patch.city &&
      !/^\d+$/.test(patch.city)
    ) {
      patch.cityName = patch.city;
      patch.cityTbl = { ...(current.cityTbl ?? {}), cityName: patch.city };
    }

    if (this.isPlainObject(patch.state) && patch.state.stateName) {
      patch.stateTbl = { ...(current.stateTbl ?? {}), ...patch.state };
      if (patch.state.id) {
        patch.state = patch.state.id;
      }
    }

    if (
      this.isPlainObject(patch.educationLevel) &&
      patch.educationLevel.id != null
    ) {
      patch.educationLevel = patch.educationLevel.id;
    }

    return patch;
  }

  private isPlainObject(value: unknown): value is Record<string, any> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private isEntityId(id: unknown): id is string | number {
    return (
      this.isBusinessId(id) ||
      (typeof id === 'number' && Number.isFinite(id) && id > 0)
    );
  }

  private resetStepControls() {
    Object.keys(this.pitchcardFormControl.controls)
      .filter((key) => key !== 'pitchcardType')
      .forEach((key) => this.pitchcardFormControl.removeControl(key));
  }

  private normalizePitchCardType(
    type?: PitchCardTypeEnum | string | null
  ): PitchCardTypeEnum {
    return (
      this.toSupportedPitchCardType(type) ??
      this.toSupportedPitchCardType(this.cardPackageService.selectedType) ??
      PitchCardTypeEnum.Employee
    );
  }

  private toSupportedPitchCardType(
    type?: PitchCardTypeEnum | string | null
  ): PitchCardTypeEnum | null {
    const value = String(type ?? '').toLowerCase();

    if (value === PitchCardTypeEnum.Service) {
      return PitchCardTypeEnum.Nonprofit;
    }

    const supportedTypes = [
      PitchCardTypeEnum.Employee,
      PitchCardTypeEnum.Resume,
      PitchCardTypeEnum.Nonprofit,
      PitchCardTypeEnum.EmployerPortal
    ];
    const supported = supportedTypes.find(
      (candidate) => candidate.toLowerCase() === value
    );

    if (supported) {
      return supported;
    }

    return null;
  }

  getEmployeeGroup() {
    if (this.shouldShowBillingStep()) {
      this.pitchcardFormControl.addControl(
        String(Step.Billing),
        this.getBilling()
      );
    }
    this.pitchcardFormControl.addControl(
      String(Step.Information),
      this.createInformationGroup(PitchCardTypeEnum.Employee)
    );
    this.pitchcardFormControl.addControl(
      String(Step.About),
      this.createAboutGroup(PitchCardTypeEnum.Employee)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Uploads),
      this.createUploadsGroup(PitchCardTypeEnum.Employee)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Pitch),
      this.createPitchGroup(PitchCardTypeEnum.Employee)
    );
  }

  getResumeGroup() {
    this.pitchcardFormControl.addControl(
      String(Step.Information),
      this.createInformationGroup(PitchCardTypeEnum.Resume)
    );
    this.pitchcardFormControl.addControl(
      String(Step.About),
      this.createAboutGroup(PitchCardTypeEnum.Resume)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Employment),
      this.createEmploymentGroup()
    );
    this.pitchcardFormControl.addControl(
      String(Step.Uploads),
      this.createUploadsGroup(PitchCardTypeEnum.Resume)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Pitch),
      this.createPitchGroup(PitchCardTypeEnum.Resume)
    );
  }

  getNonprofitGroup() {
    if (this.shouldShowBillingStep()) {
      this.pitchcardFormControl.addControl(
        String(Step.Billing),
        this.getBilling()
      );
    }
    this.pitchcardFormControl.addControl(
      String(Step.Information),
      this.createInformationGroup(PitchCardTypeEnum.Nonprofit)
    );
    this.pitchcardFormControl.addControl(
      String(Step.About),
      this.createAboutGroup(PitchCardTypeEnum.Nonprofit)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Uploads),
      this.createUploadsGroup(PitchCardTypeEnum.Nonprofit)
    );
    this.pitchcardFormControl.addControl(
      String(Step.Pitch),
      this.createPitchGroup(PitchCardTypeEnum.Nonprofit)
    );
  }

  getStepperSteps(): { value: Step; label: string; icon: string }[] {
    return this.getStepOrder().flatMap((step) => {
      if (!this.pitchcardFormControl.get(String(step))) {
        return [];
      }

      return [
        {
          value: step,
          label: this.getStepLabel(step),
          icon: this.getStepIcon(step)
        }
      ];
    });
  }

  private getStepOrder(): Step[] {
    const type = this.pitchcardFormControl.controls.pitchcardType.value;

    if (type === PitchCardTypeEnum.Resume) {
      return [
        Step.Information,
        Step.About,
        Step.Employment,
        Step.Uploads,
        Step.Pitch
      ];
    }

    if (
      type === PitchCardTypeEnum.Employee ||
      type === PitchCardTypeEnum.Nonprofit ||
      type === PitchCardTypeEnum.EmployerPortal
    ) {
      return [
        Step.Billing,
        Step.Information,
        Step.About,
        Step.Uploads,
        Step.Pitch
      ];
    }

    return [];
  }

  private getStepLabel(step: Step): string {
    const type = this.pitchcardFormControl.controls.pitchcardType.value;

    if (step === Step.Uploads && type === PitchCardTypeEnum.Resume) {
      return 'Files';
    }

    const labels: Partial<Record<Step, string>> = {
      [Step.Billing]: 'Billing',
      [Step.Information]: 'Information',
      [Step.About]: 'About Me',
      [Step.Employment]: 'Employment',
      [Step.Uploads]: 'Uploads',
      [Step.Pitch]: 'Pitch'
    };

    return labels[step] ?? String(step);
  }

  private getStepIcon(step: Step): string {
    const type = this.pitchcardFormControl.controls.pitchcardType.value;

    if (step === Step.Uploads && type === PitchCardTypeEnum.Resume) {
      return 'pi-file';
    }

    const icons: Partial<Record<Step, string>> = {
      [Step.Billing]: 'pi-credit-card',
      [Step.Information]: 'pi-id-card',
      [Step.About]: 'pi-user',
      [Step.Employment]: 'pi-briefcase',
      [Step.Uploads]: 'pi-images',
      [Step.Pitch]: 'pi-video',
      [Step.Position]: 'pi-sitemap'
    };

    return icons[step] ?? 'pi-circle';
  }

  private createInformationGroup(type: PitchCardTypeEnum) {
    const business = this.business ?? {};
    const isEmployerPortal = type === PitchCardTypeEnum.EmployerPortal;
    const contactPhone = business.contactPhone ?? {
      nationalNumber: business.contactNumber?.replace(/\D/g, ''),
      countryIsoCode: 'US'
    };
    const businessNameValidators =
      type === PitchCardTypeEnum.Resume || isEmployerPortal
        ? []
        : [Validators.required, Validators.maxLength(50)];
    const nameValidators =
      type === PitchCardTypeEnum.Employee || isEmployerPortal
        ? [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(35)
          ]
        : [
            Validators.required,
            Validators.maxLength(
              type === PitchCardTypeEnum.Resume || isEmployerPortal ? 35 : 50
            )
          ];

    return new FormGroup({
      primaryPitchColor: new FormControl(
        business.primaryPitchColor,
        isEmployerPortal ? [] : Validators.required
      ),
      preferredIntroMethod: new FormControl(
        business.preferredIntroMethod ?? 0,
        isEmployerPortal ? [] : Validators.required
      ),
      businessType: new FormControl(
        business.businessType ?? type,
        Validators.required
      ),
      title: new FormControl(
        business.title,
        isEmployerPortal ? [] : Validators.maxLength(50)
      ),
      businessName: new FormControl(
        business.businessName,
        businessNameValidators
      ),
      name: new FormControl(business.name, nameValidators),
      isHideTitle: new FormControl(business.isHideTitle ?? false),
      alias: new FormControl(business.alias?.split('?userId=')[0] ?? null),
      email: new FormControl(business.email, [
        Validators.required,
        Validators.pattern(AppSettings.EMAIL_PATTERN)
      ]),
      contactNumber: new FormControl(business.contactNumber),
      contactPhone: new FormControl(
        contactPhone,
        CustomValidators.phonePattternValidator()
      ),
      textingPhone: new FormControl(
        business.textingPhone ?? business.textingNumber,
        isEmployerPortal ? [] : CustomValidators.phonePattternValidator(false)
      ),
      textingNumber: new FormControl(business.textingNumber),
      country: new FormControl(business.country),
      address: new FormControl(business.address, Validators.required),
      addressSecondLine: new FormControl(business.addressSecondLine),
      latitude: new FormControl(business.latitude),
      longitude: new FormControl(business.longitude),
      state: new FormControl(
        business.state === DefaultCityStateEnum.state ? '' : business.state,
        Validators.required
      ),
      city: new FormControl(
        business.city === DefaultCityStateEnum.city ? '' : business.city,
        Validators.required
      ),
      zip: new FormControl(business.zip, [
        Validators.required,
        Validators.maxLength(7),
        Validators.pattern(
          business.country?.code === 'CA'
            ? AppSettings.CANADA_ZIPCODE_PATTERN
            : AppSettings.US_ZIPCODE_PATTERN
        )
      ]),
      isHideAddress: new FormControl(business.isHideAddress ?? false),
      isHideBusinessName: new FormControl(business.isHideBusinessName ?? false),
      isHideContactNumber: new FormControl(
        business.isHideContactNumber ?? false
      ),
      isHideEmail: new FormControl(business.isHideEmail ?? false),
      isHideReviews: new FormControl(business.isHideReviews ?? false),
      isHideSalary: new FormControl(business.isHideSalary ?? false),
      isHideTextNumber: new FormControl(business.isHideTextNumber ?? false),
      reviewedAddress: new FormControl(true),
      introductionOptions: new FormGroup({
        extraPhone: new FormControl(
          business.extraPhone ??
            business.contactPhone ?? {
              nationalNumber: (
                business.extraPhoneNumber ??
                business.contactNumber ??
                business.contactPhoneNumber
              )?.replace(/\D/g, ''),
              countryIsoCode: 'US'
            },
          isEmployerPortal ? [] : CustomValidators.phonePattternValidator(false)
        ),
        introText: new FormControl(
          business.introText ?? '',
          !isEmployerPortal && (business.isEnabledIntroText ?? true)
            ? introTextRequired
            : []
        ),
        isEnabledIntroText: new FormControl(
          isEmployerPortal ? false : (business.isEnabledIntroText ?? true)
        ),
        referralEmail: new FormControl(
          business.referralEmail ?? business.email
        ),
        preferredIntroMethod: new FormControl(
          business.preferredIntroMethod ?? 0
        )
      }),
      referralIntroductionOptions: new FormGroup({
        includeReferralInstructions: new FormControl(
          business.includeReferralInstructions ?? false
        ),
        referralRequestMessage: new FormControl(business.referralRequestMessage)
      })
    });
  }

  private createAboutGroup(type: PitchCardTypeEnum) {
    const business = this.business ?? {};
    const isResume = type === PitchCardTypeEnum.Resume;
    const isEmployerPortal = type === PitchCardTypeEnum.EmployerPortal;
    const requiresBusinessTags = type === PitchCardTypeEnum.Employee;

    return new FormGroup({
      websiteLink: new FormControl(
        business.websiteLink ?? '',
        Validators.pattern(AppSettings.WEBSITE_PATTERN)
      ),
      calendarLink: new FormControl(
        business.calendarLink,
        Validators.pattern(AppSettings.WEBSITE_PATTERN)
      ),
      websiteLinkCallToAction: new FormControl(
        business.websiteLinkCallToAction
      ),
      calendarLinkCallToAction: new FormControl(
        business.calendarLinkCallToAction
      ),
      links: new FormControl(business.links),
      organizationPartners: new FormControl(
        business.organizationPartners ?? []
      ),
      workingHours: new FormControl(
        isResume ? [] : (business.workingHours ?? [])
      ),
      businessTags: new FormControl(
        business.businessTags ?? [],
        requiresBusinessTags ? Validators.required : []
      ),
      industries: new FormControl(
        business.industries ?? [],
        isEmployerPortal ? Validators.required : []
      ),
      description: new FormControl(
        business.pricingModel ?? null,
        Validators.maxLength(2000000)
      ),
      positions: new FormControl(business.positions ?? [])
    });
  }

  private createUploadsGroup(type: PitchCardTypeEnum) {
    const business = this.business ?? {};
    const isResume = type === PitchCardTypeEnum.Resume;
    const isEmployerPortal = type === PitchCardTypeEnum.EmployerPortal;

    return new FormGroup({
      businessType: new FormControl(
        business.businessType ?? type,
        Validators.required
      ),
      employeePictureFileIds: new FormControl(business.employeePictureFileIds),
      employeePictureFileUrl: new FormControl(business.employeePictureFileUrl),
      employeePictureThumnailIds: new FormControl(
        business.employeePictureThumnailIds
      ),
      employeePictureThumnailUrl: new FormControl(
        business.employeePictureThumnailUrl
      ),
      videoCoverImageFileId: new FormControl(
        business.videoCoverImageFileId,
        isEmployerPortal ? [] : Validators.required
      ),
      videoCoverImageThumbnailId: new FormControl(
        business.videoCoverImageThumbnailId
      ),
      videoCoverImageThumbnailUrl: new FormControl(
        business.videoCoverImageThumbnailUrl
      ),
      resumeFileId: new FormControl(
        business.resumeFileId,
        isResume ? Validators.required : []
      ),
      resumeFileUrl: new FormControl(
        business.resumeFileUrl,
        isResume ? Validators.required : []
      ),
      businessLogoFilelId: new FormControl(business.businessLogoFilelId),
      businessLogoThumbnailId: new FormControl(
        business.businessLogoThumbnailId
      ),
      businessLogoThumbnailUrl: new FormControl(
        business.businessLogoThumbnailUrl
      ),
      isRoundBusinessLogoThumbnail: new FormControl(
        business.isRoundBusinessLogoThumbnail ?? false
      ),
      organizationLogoFilelId: new FormControl(
        business.organizationLogoFilelId,
        isEmployerPortal ? Validators.required : []
      ),
      organizationLogoThumbnailId: new FormControl(
        business.organizationLogoThumbnailId
      ),
      organizationLogoThumbnailUrl: new FormControl(
        business.organizationLogoThumbnailUrl
      ),
      reviews: new FormControl(business.reviews)
    });
  }

  private createEmploymentGroup() {
    const business = this.business ?? {};
    const educationLevel = educationLevelOption(business.educationLevel);
    const requiresInstitutions =
      educationLevel.id >= EducationLevel.TradeSchool;

    return new FormGroup({
      positions: new FormControl(business.positions ?? [], Validators.required),
      educationalInstitutions: new FormControl(
        {
          value: business.educationalInstitutions ?? [],
          disabled: !requiresInstitutions
        },
        requiresInstitutions ? Validators.required : []
      ),
      educationLevel: new FormControl(educationLevel, Validators.required),
      workType: new FormControl(business.workType ?? 1),
      workTypes: new FormControl(business.workTypes ?? [], Validators.required),
      hasMilitaryService: new FormControl(business.hasMilitaryService ?? false)
    });
  }

  private createPitchGroup(type: PitchCardTypeEnum) {
    const business = this.business ?? {};
    const isEmployerPortal = type === PitchCardTypeEnum.EmployerPortal;
    const videoFileId =
      business.videoFileId ?? AppSettings.commonPlaceholderVideoID;
    const hasRealVideo = this.hasRealPitchVideo(business);
    const savedDelivery = Number(business.deliveryType);
    const deliveryType = isEmployerPortal
      ? 1
      : savedDelivery === 0 || savedDelivery === 1
        ? savedDelivery
        : hasRealVideo
          ? 1
          : 0;
    const videoRequired = deliveryType === 1 && !isEmployerPortal;
    const defaultVideoUrl = (control: AbstractControl) =>
      control.value === AppSettings.commonPlaceholderVideoUrl
        ? { default: true }
        : null;

    return new FormGroup({
      writtenElevatorPitchScript: new FormControl(
        business.writtenElevatorPitchScript ?? '',
        isEmployerPortal ? [] : Validators.required
      ),
      isPitchScriptHelperActive: new FormControl(
        isEmployerPortal ? false : (business.isPitchScriptHelperActive ?? true)
      ),
      deliveryType: new FormControl(deliveryType),
      videoFileId: new FormControl(
        videoFileId,
        videoRequired ? Validators.required : []
      ),
      isMirrorVideo: new FormControl(business.isMirrorVideo ?? false),
      videoFileUrl: new FormControl(
        business.videoFileUrl ?? AppSettings.commonPlaceholderVideoUrl,
        videoRequired ? [Validators.required, defaultVideoUrl] : []
      ),
      videoThumbnailUrl: new FormControl(
        business.videoThumbnailUrl ??
          (hasRealVideo
            ? `https://image.mux.com/${videoFileId}/thumbnail.jpg`
            : ''),
        videoRequired ? Validators.required : []
      )
    });
  }

  private hasRealPitchVideo(business: any): boolean {
    const videoFileId = business?.videoFileId;
    const videoFileUrl = business?.videoFileUrl;
    const placeholders = new Set([
      AppSettings.commonPlaceholderVideoID,
      AppSettings.commonPlaceholderPlaybackID,
      AppSettings.commonPlaceholderVideoUrl,
      AppSettings.jobPlaceholderVideoID,
      AppSettings.jobPlaceholderPlaybackID,
      AppSettings.jobPlaceholderVideoUrl
    ]);

    return (
      (!!videoFileId && !placeholders.has(videoFileId)) ||
      (!!videoFileUrl && !placeholders.has(videoFileUrl))
    );
  }

  private getBilling() {
    const business = this.business;
    const useBillingAddress = business?.useBillingAddress ?? false;
    const address = business?.businessBillingAddress;
    const disabled = useBillingAddress;
    const validators = disabled ? [] : Validators.required;

    return new FormGroup({
      useBillingAddress: new FormControl(!useBillingAddress),
      address: new FormControl(
        { value: address?.address ?? null, disabled },
        validators
      ),
      zip: new FormControl(
        { value: address?.zip ?? null, disabled },
        validators
      ),
      city: new FormControl(
        { value: address?.city ?? null, disabled },
        validators
      ),
      state: new FormControl(
        { value: address?.state ?? null, disabled },
        validators
      )
    });
  }
}
